// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import { xIsAuthenticated } from "@steiner/App/Store/Selectors/SessionSelectors";
import { getSettingsCollection } from "@steiner/App/Store/Selectors/SettingsSelectors";
import { getSignInRedirectUrl } from "@steiner/Overrides/Widgets/Common/HeaderSignIn";
import { mergeWithCss } from "@steiner/XDL/Utilities";
import { xTranslate } from "@steiner/XDL/Utilities/translate";
import React, { useState } from "react";
import { FC } from "react";

interface OwnProps {
    extendedStyles?: LoginToAccessAlertStyles;
}

export interface LoginToAccessAlertStyles {
    container?: InjectableCss;
    message?: TypographyPresentationProps;
    secondPartMessage?: TypographyPresentationProps;
    viewLink?: LinkPresentationProps;
}

export const loginToAccessAlertStyles: LoginToAccessAlertStyles = {};

const LoginToAccessAlert: FC<OwnProps> = ({ extendedStyles }) => {
    const { isAuthenticated, signInUrl, currentLocation, createAccountUrl, contactUsUrl } = useAppSelector(state => ({
        isAuthenticated: xIsAuthenticated(state),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        createAccountUrl: getPageLinkByPageType(state, "CreateAccountPage")?.url,
        contactUsUrl: getSettingsCollection(state).customSharedSettings.contactUsUrl || "/ContactUs",
        currentLocation: getLocation(state),
    }));

    const [styles] = useState(() => mergeWithCss(loginToAccessAlertStyles, extendedStyles));
    const history = useHistory();

    const redirectToSignInPage = () => {
        history.push(
            getSignInRedirectUrl({ signInUrl, pathname: currentLocation.pathname, search: currentLocation.search }),
        );
    };

    const redirectToCreateAccountPage = () => {
        history.push(createAccountUrl || "/MyAccount");
    };

    const redirectToContactUsPage = () => {
        if (!contactUsUrl) {
            return;
        }

        history.push(contactUsUrl);
    };

    if (isAuthenticated) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="LoginToAccessAlert">
            <Typography {...styles.message}>
                {xTranslate(
                    `{0} or {1} to view stock quantity and your pricing.`,
                    <Link onClick={redirectToSignInPage} {...styles.viewLink} key={`${uniqueId()}-Log In`}>
                        {translate("Log In")}
                    </Link>,
                    <Link
                        onClick={redirectToCreateAccountPage}
                        {...styles.viewLink}
                        key={`${uniqueId()}-Create an Account`}
                    >
                        {translate("Create an Account")}
                    </Link>,
                )}
                <Typography {...styles.secondPartMessage}>
                    {xTranslate(
                        `{1} Please {2} for assistance if outside our sales territory.`,
                        <Typography weight="bold" forwardAs="span">
                            {translate(
                                "Steiner serves territories in Northern Illinois, Northwest Indiana and Southern Wisconsin.",
                            )}
                        </Typography>,
                        <Link onClick={redirectToContactUsPage} {...styles.viewLink} key={`${uniqueId()}-Contact Us`}>
                            {translate("Contact Us")}
                        </Link>,
                    )}
                </Typography>
            </Typography>
        </StyledWrapper>
    );
};

export default LoginToAccessAlert;
